import { useCallback, ReactNode } from 'react';
import {
  toast,
  ToastContainer,
  type ToastContainerProps,
  type ToastOptions,
} from 'react-toastify';
import {
  ErrorIcon, InfoIcon, SuccessIcon, WarningIcon,
} from '../../icons';
import SnackbarMessage from '../../components/Snackbar';

export interface IShowSnackbarParams {
  text?: string | null;
  title?: string | null;
  autoClose?: number;
}

export enum AlertType {
  Error,
  Warning,
  Info,
  Success
}

const ICON_PROPS = { width: 38, height: 38 };

const SUCCESS_ICON: ToastOptions['icon'] = <SuccessIcon {...ICON_PROPS} />;
const ERROR_ICON: ToastOptions['icon'] = <ErrorIcon {...ICON_PROPS} />;
const INFO_ICON: ToastOptions['icon'] = <InfoIcon {...ICON_PROPS} />;
const WARNING_ICON: ToastOptions['icon'] = <WarningIcon {...ICON_PROPS} />;

const useSnackbar = () => {
  const showSuccess = useCallback((params: IShowSnackbarParams) => {
    const { text = null, title = null, autoClose = 5000 } = params;
    toast.success(
      <SnackbarMessage body={text} title={title} />,
      { icon: SUCCESS_ICON, autoClose },
    );
  }, []);
  const showError = useCallback((params: IShowSnackbarParams) => {
    const { text = null, title = null, autoClose = 7000 } = params;
    toast.error(
      <SnackbarMessage body={text} title={title} />,
      { icon: ERROR_ICON, autoClose },
    );
  }, []);
  const showInfo = useCallback((params: IShowSnackbarParams) => {
    const { text = null, title = null, autoClose = 5000 } = params;
    toast.info(
      <SnackbarMessage body={text} title={title} />,
      { icon: INFO_ICON, autoClose },
    );
  }, []);
  const showWarning = useCallback((params: IShowSnackbarParams) => {
    const { text = null, title = null, autoClose = 7000 } = params;
    toast.warning(
      <SnackbarMessage body={text} title={title} />,
      { icon: WARNING_ICON, autoClose },
    );
  }, []);
  const getSnackbar = useCallback((
    params: Omit<ToastContainerProps, 'theme'> = {},
  ): ReactNode => (
    <ToastContainer
      closeButton={false}
      {...params}
    />
  ), []);
  return {
    showSuccess,
    showError,
    showInfo,
    showWarning,
    getSnackbar,
  };
};

export default useSnackbar;
